import { ResourceType } from '@cotera/api';

/**
 * A resource seen from the outside: what it is, who owns it, what it is called,
 * and where it is filed. Every question a call site asks when it does not care
 * which of the seven types it is holding.
 *
 * Deliberately a *view*, not the domain model. Half the call sites only ever
 * have a list row, never a full `resource/get` response, and hydrating an
 * `Agent` to read a name builds a TipTap editor. `Agent`, `Folder`, `Dataset`
 * and friends keep their own constructors; this sits alongside them.
 *
 * The members here are the ones all seven types can answer honestly. `orgId` and
 * `status` are not among them — folder bodies carry no org id, folders have no
 * archived state, `Workflow.status` is a publish axis, and workflow triggers
 * have `enabled` instead.
 */
export interface ResourceView {
  readonly kind: ResourceKindMeta;
  readonly id: string;
  /**
   * The user this resource belongs to. Chats spell it `actorId` and everything
   * else `ownerId`, which is the distinction this hides.
   */
  readonly ownerActorId: string;
  /** Per-type rule, defined once in that type's kind. Null = genuinely unnamed. */
  displayName(): string | null;
  /** {@link displayName}, falling back to the type's "Untitled …" label. */
  title(): string;
  /** The folder containing this resource, or null when it is unfiled. */
  folderId(): string | null;
  readonly createdAt: Date;
  readonly updatedAt: Date;
}

const toDate = (value: Date | string): Date =>
  typeof value === 'string' ? new Date(value) : value;

/**
 * How a resource type shows up as a chat artifact: the artifact-type string the
 * renderer registry is keyed by, and the payload key that carries the resource
 * id. Null when the type has no artifact renderer, so it can only be linked to.
 */
export type ResourceArtifactKind = {
  readonly type: string;
  readonly idKey: string;
};

/**
 * How a resource type presents itself: labels, routes, artifact wiring,
 * capabilities.
 *
 * Data only. That is what lets {@link ResourceView.kind} be this type while the
 * definitions below stay parameterised by their own body — a type with function
 * members would not be assignable across resource types.
 */
export type ResourceKindMeta = {
  readonly type: ResourceType;
  /** Singular, title-case: "Chat", "Trigger". */
  readonly label: string;
  /** Plural, title-case: "Chats", "Triggers". */
  readonly pluralLabel: string;
  /** Shown in place of a name when a resource has none. */
  readonly untitledLabel: string;
  /** URL segment for one resource's page, e.g. `chats` in `/acme/chats/:id`. */
  readonly itemRouteSegment: string;
  /** URL segment for the type's list page. */
  readonly listRouteSegment: string;
  /** Key holding this type's body in a `resource/get` response. */
  readonly outputKey: string;
  readonly artifact: ResourceArtifactKind | null;
  readonly capabilities: {
    /** Whether the type gets its own row at the root of the Library. */
    readonly showsInLibraryRoot: boolean;
  };
};

/** Timestamps and an id, which every resource wire shape carries. */
type Identified = {
  readonly id: string;
  readonly createdAt: Date | string;
  readonly updatedAt: Date | string;
};

/** Placement, as a `resource/get` response carries it. */
type WithFolderMeta = { readonly meta?: { readonly folderId?: string | null } };

/** Placement, as a folder or list row carries it. */
type WithFolderId = { readonly folderId?: string | null };

/**
 * Everything one resource type knows about itself.
 *
 * This is the single place a resource type is declared. `TBody` is the shape it
 * carries on the wire — the same inside a `resource/get` response and a list
 * row, since only the wrapper differs — and `read` / `readItem` are how the type
 * recognises itself in either. They return null for another type's payload,
 * which is what lets the dispatchers below be two lines with no switch.
 */
type ResourceKindDefinition<TBody extends Identified> = ResourceKindMeta & {
  /** The user this resource belongs to. Chats say `actorId`, everything else `ownerId`. */
  readonly ownerActorId: (body: TBody) => string;
  /** What to call it, by this type's own rule. Null when genuinely unnamed. */
  readonly displayName: (body: TBody) => string | null;
  /** This type's view of a `resource/get` response, or null if it is not ours. */
  readonly read: (output: ResourceOutputLike) => ResourceView | null;
  /** This type's view of a folder or list row, or null if it is not ours. */
  readonly readItem: (item: ResourceFolderItemLike) => ResourceView | null;
};

class WireResourceView<TBody extends Identified> implements ResourceView {
  readonly kind: ResourceKindMeta;
  readonly createdAt: Date;
  readonly updatedAt: Date;

  constructor(
    private readonly definition: ResourceKindDefinition<TBody>,
    private readonly body: TBody,
    private readonly _folderId: string | null
  ) {
    this.kind = definition;
    this.createdAt = toDate(body.createdAt);
    this.updatedAt = toDate(body.updatedAt);
  }

  get id(): string {
    return this.body.id;
  }

  get ownerActorId(): string {
    return this.definition.ownerActorId(this.body);
  }

  displayName(): string | null {
    return this.definition.displayName(this.body);
  }

  title(): string {
    return this.displayName() ?? this.definition.untitledLabel;
  }

  folderId(): string | null {
    return this._folderId;
  }
}

/** The view for a body found inside a `resource/get` response. */
const viewOfBody = <TBody extends Identified>(
  definition: ResourceKindDefinition<TBody>,
  body: TBody,
  folderId: string | null
): ResourceView => new WireResourceView(definition, body, folderId);

const trimmedOrNull = (value: string | null | undefined): string | null => {
  if (value === undefined || value === null) {
    return null;
  }
  const trimmed = value.trim();
  return trimmed === '' ? null : trimmed;
};

/**
 * An `agent-<hex>` key is generated when an agent has no name yet, so it is an
 * id wearing a name's clothes and must not be shown as one.
 */
const AUTO_GENERATED_AGENT_KEY = /^agent-[a-f0-9]{16,}$/i;

const usableAgentKey = (agentKey: string | null | undefined): string | null => {
  const trimmed = trimmedOrNull(agentKey);
  return trimmed === null || AUTO_GENERATED_AGENT_KEY.test(trimmed)
    ? null
    : trimmed;
};

// --- One block per resource type. Adding a type means adding one of these. ---

type ChatBody = Identified & {
  readonly actorId: string;
  readonly title?: string | null;
};
const CHAT: ResourceKindDefinition<ChatBody> = {
  type: ResourceType.Chat,
  label: 'Chat',
  pluralLabel: 'Chats',
  untitledLabel: 'Untitled chat',
  itemRouteSegment: 'chats',
  listRouteSegment: 'chats',
  outputKey: 'chat',
  artifact: null,
  capabilities: { showsInLibraryRoot: false },
  ownerActorId: (body) => body.actorId,
  displayName: (body) => trimmedOrNull(body.title),
  read: (output) =>
    output.type === 'chat'
      ? viewOfBody(CHAT, output.chat, folderFromMeta(output))
      : null,
  readItem: (item) =>
    item.type === 'chat'
      ? viewOfBody(CHAT, item.data, item.data.folderId ?? null)
      : null,
};

type AgentBody = Identified & {
  readonly ownerId: string;
  readonly title?: string | null;
  readonly agentKey?: string | null;
};
const AGENT: ResourceKindDefinition<AgentBody> = {
  type: ResourceType.Agent,
  label: 'Agent',
  pluralLabel: 'Agents',
  untitledLabel: 'Untitled agent',
  itemRouteSegment: 'agent',
  listRouteSegment: 'agents',
  outputKey: 'agent',
  artifact: { type: 'agent', idKey: 'agentId' },
  capabilities: { showsInLibraryRoot: true },
  ownerActorId: (body) => body.ownerId,
  displayName: (body) =>
    trimmedOrNull(body.title) ?? usableAgentKey(body.agentKey),
  read: (output) =>
    output.type === 'agent'
      ? viewOfBody(AGENT, output.agent, folderFromMeta(output))
      : null,
  readItem: (item) =>
    item.type === 'agent'
      ? viewOfBody(AGENT, item.data, item.data.folderId ?? null)
      : null,
};

type FolderBody = Identified & {
  readonly ownerId: string;
  readonly parentId: string | null;
  readonly name: string;
};
const FOLDER: ResourceKindDefinition<FolderBody> = {
  type: ResourceType.Folder,
  label: 'Folder',
  pluralLabel: 'Folders',
  untitledLabel: 'Untitled folder',
  itemRouteSegment: 'folders',
  listRouteSegment: 'folders',
  outputKey: 'folder',
  artifact: { type: 'folder', idKey: 'folderId' },
  capabilities: { showsInLibraryRoot: true },
  ownerActorId: (body) => body.ownerId,
  displayName: (body) => trimmedOrNull(body.name),
  // A folder is placed by its parent rather than by `meta`.
  read: (output) =>
    output.type === 'folder'
      ? viewOfBody(FOLDER, output.folder, output.folder.parentId)
      : null,
  readItem: (item) =>
    item.type === 'folder'
      ? viewOfBody(FOLDER, item.data, item.data.parentId)
      : null,
};

type DocumentBody = Identified & {
  readonly ownerId: string;
  readonly title?: string | null;
};
const DOCUMENT: ResourceKindDefinition<DocumentBody> = {
  type: ResourceType.Document,
  label: 'Document',
  pluralLabel: 'Documents',
  untitledLabel: 'Untitled document',
  itemRouteSegment: 'documents',
  listRouteSegment: 'documents',
  outputKey: 'document',
  artifact: { type: 'document', idKey: 'documentId' },
  capabilities: { showsInLibraryRoot: true },
  ownerActorId: (body) => body.ownerId,
  displayName: (body) => trimmedOrNull(body.title),
  read: (output) =>
    output.type === 'document'
      ? viewOfBody(DOCUMENT, output.document, folderFromMeta(output))
      : null,
  readItem: (item) =>
    item.type === 'document'
      ? viewOfBody(DOCUMENT, item.data, item.data.folderId ?? null)
      : null,
};

type NamedBody = Identified & {
  readonly ownerId: string;
  readonly name: string;
};
const WORKFLOW: ResourceKindDefinition<NamedBody> = {
  type: ResourceType.Workflow,
  label: 'Workflow',
  pluralLabel: 'Workflows',
  untitledLabel: 'Untitled workflow',
  itemRouteSegment: 'workflows',
  listRouteSegment: 'workflows',
  outputKey: 'workflow',
  artifact: { type: 'workflow', idKey: 'workflowId' },
  capabilities: { showsInLibraryRoot: true },
  ownerActorId: (body) => body.ownerId,
  displayName: (body) => trimmedOrNull(body.name),
  read: (output) =>
    output.type === 'workflow'
      ? viewOfBody(WORKFLOW, output.workflow, folderFromMeta(output))
      : null,
  readItem: (item) =>
    item.type === 'workflow'
      ? viewOfBody(WORKFLOW, item.data, item.data.folderId ?? null)
      : null,
};

const DATASET: ResourceKindDefinition<NamedBody> = {
  type: ResourceType.Dataset,
  label: 'Dataset',
  pluralLabel: 'Datasets',
  untitledLabel: 'Untitled dataset',
  itemRouteSegment: 'datasets',
  listRouteSegment: 'datasets',
  outputKey: 'dataset',
  artifact: { type: 'dataset', idKey: 'datasetId' },
  capabilities: { showsInLibraryRoot: true },
  ownerActorId: (body) => body.ownerId,
  displayName: (body) => trimmedOrNull(body.name),
  read: (output) =>
    output.type === 'dataset'
      ? viewOfBody(DATASET, output.dataset, folderFromMeta(output))
      : null,
  readItem: (item) =>
    item.type === 'dataset'
      ? viewOfBody(DATASET, item.data, item.data.folderId ?? null)
      : null,
};

/**
 * A `resource/get` response nests a trigger's schedule and event name; a list
 * row flattens them. Both spellings are optional here so one body covers both.
 */
type WorkflowTriggerBody = Identified & {
  readonly ownerId: string;
  readonly name: string;
  readonly triggerKind: 'cron' | 'webhook' | 'event' | 'dataset';
  readonly cron?: { readonly schedule: string } | null;
  readonly event?: { readonly eventName: string } | null;
  readonly cronSchedule?: string | null;
  readonly eventName?: string | null;
};
const WORKFLOW_TRIGGER: ResourceKindDefinition<WorkflowTriggerBody> = {
  type: ResourceType.WorkflowTrigger,
  label: 'Trigger',
  pluralLabel: 'Triggers',
  untitledLabel: 'Untitled trigger',
  itemRouteSegment: 'triggers',
  listRouteSegment: 'triggers',
  outputKey: 'workflowTrigger',
  artifact: null,
  capabilities: { showsInLibraryRoot: false },
  ownerActorId: (body) => body.ownerId,
  // A trigger created inline often has no name, so it describes what it does.
  displayName: (body) => {
    const named = trimmedOrNull(body.name);
    if (named !== null) {
      return named;
    }
    switch (body.triggerKind) {
      case 'cron':
        return trimmedOrNull(body.cron?.schedule ?? body.cronSchedule);
      case 'event':
        return trimmedOrNull(body.event?.eventName ?? body.eventName);
      case 'webhook':
        return 'Webhook';
      case 'dataset':
        return 'Dataset change';
    }
  },
  read: (output) =>
    output.type === 'workflow_trigger'
      ? viewOfBody(
          WORKFLOW_TRIGGER,
          output.workflowTrigger,
          folderFromMeta(output)
        )
      : null,
  readItem: (item) =>
    item.type === 'workflow_trigger'
      ? viewOfBody(WORKFLOW_TRIGGER, item.data, item.data.folderId ?? null)
      : null,
};

const folderFromMeta = (output: WithFolderMeta): string | null =>
  output.meta?.folderId ?? null;

/**
 * The one registry. A mapped type over {@link ResourceType} with no `as` cast,
 * so a new enum member is a compile error here — and, because the wire unions
 * and both dispatchers are derived from it, nowhere else.
 */
const RESOURCE_KIND_DEFINITIONS = {
  [ResourceType.Chat]: CHAT,
  [ResourceType.Agent]: AGENT,
  [ResourceType.Folder]: FOLDER,
  [ResourceType.Document]: DOCUMENT,
  [ResourceType.Workflow]: WORKFLOW,
  [ResourceType.Dataset]: DATASET,
  [ResourceType.WorkflowTrigger]: WORKFLOW_TRIGGER,
} satisfies { readonly [K in ResourceType]: ResourceKindDefinition<never> };

/** Presentation and routing for a resource type. Total by construction. */
export const resourceKind = (type: ResourceType): ResourceKindMeta =>
  RESOURCE_KIND_DEFINITIONS[type];

/** Every declared kind, for building menus and exhaustive test tables. */
export const ALL_RESOURCE_KINDS: readonly ResourceKindMeta[] = Object.values(
  RESOURCE_KIND_DEFINITIONS
);

const ALL_DEFINITIONS = Object.values(RESOURCE_KIND_DEFINITIONS);

/**
 * The parts of a `resource/get` response the readers need, derived from the
 * definitions above so a payload key is written exactly once.
 *
 * Structural rather than `ResourceOutput` on purpose: the client has its own
 * hand-typed mirror whose `workflow.graph` is `unknown` and whose dates are
 * `Date | string`, and the readers touch neither, so binding to one of the two
 * would force call sites to convert between shapes that agree everywhere it
 * looks.
 */
export type ResourceOutputLike =
  | ({ readonly type: 'chat'; readonly chat: ChatBody } & WithFolderMeta)
  | ({ readonly type: 'agent'; readonly agent: AgentBody } & WithFolderMeta)
  | { readonly type: 'folder'; readonly folder: FolderBody }
  | ({
      readonly type: 'document';
      readonly document: DocumentBody;
    } & WithFolderMeta)
  | ({
      readonly type: 'workflow';
      readonly workflow: NamedBody;
    } & WithFolderMeta)
  | ({
      readonly type: 'dataset';
      readonly dataset: NamedBody;
    } & WithFolderMeta)
  | ({
      readonly type: 'workflow_trigger';
      readonly workflowTrigger: WorkflowTriggerBody;
    } & WithFolderMeta);

/** The parts of a folder or list row the readers need. */
export type ResourceFolderItemLike =
  | { readonly type: 'chat'; readonly data: ChatBody & WithFolderId }
  | {
      readonly type: 'agent';
      readonly data: AgentBody & WithFolderId;
    }
  | { readonly type: 'folder'; readonly data: FolderBody }
  | {
      readonly type: 'document';
      readonly data: DocumentBody & WithFolderId;
    }
  | {
      readonly type: 'workflow';
      readonly data: NamedBody & WithFolderId;
    }
  | {
      readonly type: 'dataset';
      readonly data: NamedBody & WithFolderId;
    }
  | {
      readonly type: 'workflow_trigger';
      readonly data: WorkflowTriggerBody & WithFolderId;
    };

/**
 * Unreachable while every arm of the two wire unions above is backed by a
 * definition — which the `satisfies` on the registry enforces.
 */
const unreadable = (type: string): never => {
  throw new Error(`No resource kind could read a ${type}`);
};

/** A {@link ResourceView} over a `resource/get` response. */
export const resourceViewOf = (output: ResourceOutputLike): ResourceView => {
  for (const definition of ALL_DEFINITIONS) {
    const view = definition.read(output);
    if (view !== null) {
      return view;
    }
  }
  return unreadable(output.type);
};

/** A {@link ResourceView} over one row of a folder or list response. */
export const resourceViewOfItem = (
  item: ResourceFolderItemLike
): ResourceView => {
  for (const definition of ALL_DEFINITIONS) {
    const view = definition.readItem(item);
    if (view !== null) {
      return view;
    }
  }
  return unreadable(item.type);
};
